import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { ExpressTestsService } from "../../../../shared/services/express-tests.service";
import { ExpressTestByRequestDto, ExpressTestRequestIndicatorDto } from "../../../../shared/models/express-test/express-test-by-request.dto";
import { Guid } from "guid-typescript";
import { UsersService } from "../../../../admin/users/users.service";
import { forkJoin } from "rxjs";
import { KeyValueObject } from "../../../../shared/models/core/KeyValueObject";
import { ExpressTestLaboratoryIndicatorDto } from "../../../../shared/models/express-test/express-test-laboratory-indicator.dto";
import { ExpressTestUpdateIndicatorDto } from "../../../../shared/models/express-test/express-test-update-indicator.dto";
import { format } from "date-fns";
import { AppService } from "../../../../app.service";

@Component({
    selector: 'agreement-water-analysis',
    templateUrl: './agreement-water-analysis.component.html',
    styleUrls: [
        './agreement-water-analysis.component.scss',
        '../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
        '../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
    ]
})
export class AgreementWaterAnalysisComponent implements OnChanges {

    @Input() customerObjectId: number;
    @Input() useCardLayout: boolean = false;
    @Input() canEdit: boolean = true;

    tests: ExpressTestByRequestDto[] = [];
    performerUsers: KeyValueObject[] = [];
    laboratoryIndicators: ExpressTestLaboratoryIndicatorDto[] = [];
    selectedTestId: string;

    loading: boolean = false;
    loaded: boolean = false;
    creatingTest: boolean = false;
    editingTest: boolean = false;
    dataSaving: boolean = false;
    cardOpened: boolean = false;

    constructor(
        private expressTestsService: ExpressTestsService,
        private usersService: UsersService,
        private appService: AppService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['customerObjectId'] || !this.customerObjectId) {
            return;
        }

        this.creatingTest = false;
        this.editingTest = false;
        this.dataSaving = false;
        this.loaded = false;
        this.selectedTestId = null;
        this.tests = [];
        this.loadData();
    }

    private loadData(): void {
        const tests$ = this.expressTestsService.getTestByCustomerObject(this.customerObjectId);
        const performerUsers$ = this.usersService.getUserEngineers();
        const laboratoryIndicators$ = this.expressTestsService.getLaboratoryIndicators();

        forkJoin([tests$, performerUsers$, laboratoryIndicators$]).subscribe(([tests, performerUsers, laboratoryIndicators]) => {
            this.tests = tests.map(x => { 
                x.id = Guid.create().toString();
                x.completeDateValue = this.appService.parseDate(x.completeDateValue);
                return x; 
            });

            if (tests.length > 0) {
                this.selectedTestId = tests[0].id;
            }

            this.performerUsers = performerUsers.map(x => new KeyValueObject(x.userId, x.displayName));
            this.laboratoryIndicators = laboratoryIndicators;

            this.loaded = true;
        });
    }

    loadTests(selectedPosition: number = null) {
        this.expressTestsService.getTestByCustomerObject(this.customerObjectId).subscribe(resp => {
            this.tests = resp.map(x => { 
                x.id = Guid.create().toString(); 
                x.completeDateValue = this.appService.parseDate(x.completeDateValue);
                return x; 
            });

            if (selectedPosition != null && this.tests.length > selectedPosition) {
                this.selectedTestId = this.tests[selectedPosition].id;
            }
            else if (this.tests.length > 0 && !this.tests.some(x => x.id == this.selectedTestId)) {
                this.selectedTestId = this.tests[0].id;
            }
        });
    }

    selectTest(id: string) {
        if (!this.creatingTest && !this.editingTest && !this.dataSaving) {
            this.selectedTestId = id;
        }
    }

    createNewTest() {
        if (!this.canEdit) {
            return;
        }

        this.cardOpened = true;
        this.creatingTest = true;
        let newTest = new ExpressTestByRequestDto;
        newTest.id = Guid.create().toString();
        newTest.laboratoryRequestIndicators = this.laboratoryIndicators.map(x => {
            let indicator = new ExpressTestRequestIndicatorDto;
            indicator.name = x.laboratoryIndicatorName;
            indicator.laboratoryIndicatorId = x.laboratoryIndicatorId;
            indicator.laboratoryIndicatorCode = x.laboratoryIndicatorCode;
            return indicator;
        });
        this.selectedTestId = newTest.id;
        this.tests.push(newTest);
    }

    cancelEditingTest(test: ExpressTestByRequestDto) {
        if (this.creatingTest) {
            this.tests = this.tests.filter(x => x.id != this.selectedTestId);
            this.selectedTestId = this.tests.length > 0 ? this.tests[0].id : null;
            this.creatingTest = false;
        }
        else {
            this.editingTest = false;
            let testIndex = this.tests.findIndex(x => x.id == test.id);
            this.tests[testIndex] = test;
        }
    }

    editTest() {
        if (!this.canEdit) {
            return;
        }

        this.editingTest = true;
    }

    saveTest(test: ExpressTestByRequestDto) {
        if (!this.canEdit) {
            return;
        }

        this.dataSaving = true;
        let updateTestModels = test.laboratoryRequestIndicators.map(x => {
            let indicator = new ExpressTestUpdateIndicatorDto;
            indicator.laboratoryIndicatorCode = x.laboratoryIndicatorCode;
            indicator.value = x.value;
            indicator.postHandleValue = x.postHandleValue;
            return indicator;
        });

        this.expressTestsService.updateTestByCustomerObject(
            this.customerObjectId, this.appService.formatDate(test.completeDateValue), test.performerUserId, updateTestModels).subscribe(() => 
        {
            let selectedPosition = this.tests.findIndex(x => x.id == test.id);
            this.loadTests(selectedPosition);
            this.editingTest = false;
            this.creatingTest = false;
            this.dataSaving = false;
        }, () => this.dataSaving = false);
    }

    get selectedTest(): ExpressTestByRequestDto {
        return this.selectedTestId != null && this.tests.length > 0
            ? this.tests.find(x => x.id == this.selectedTestId)
            : null;
    }

    get excludedCompleteDates(): Date[] {
        return this.tests.filter(x => x.id != this.selectedTestId && x.completeDateValue != null && x.requestId == null).map(x => x.completeDateValue);
    }
}